import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiChartYAxisKey,
  ApiDimension,
  ApiDimensionCategory,
  ApiMetric,
  ApiMetricCategory,
  ApiOperator,
  ApiRow,
  ApiSimpleDimensionLabel,
  ApiSimpleMetricLabel,
  ApiYAxisChartType,
} from "@incendium/api";
import { alpha, darken, lighten } from "@mui/material";
import { mergeArraysByKey, sortAlpha, uniqueArray } from "Helpers/arrays";
import { enumToArray } from "Helpers/enumToText";
import { percentageChange, percentageOf } from "Helpers/percentage";
import { IChart, IYKey, TChartData, TChartDataCompare } from "Interfaces";
import { AttributionMetric } from "../types/types";
import produce from "immer";
import { isArrayEmpty } from "Helpers/isEmpty";
import { formatNumber } from "Helpers/numbers";
import { camelToText } from "Helpers/camelToText";
import {
  metricByAttribution,
  metricConfigByName,
} from "../services/metricFormatter";
// import { mergeMapsSortByKey } from "Helpers/maps";

export const getChartColor = (
  index: number,
  colours: string[],
  opactity?: number,
) => {
  const value = Math.floor(index / (colours.length - 1));
  const n = 0 + Number(`0.${value}`) * 1.3;
  const colour = alpha(colours[index % (colours.length - 1)], opactity || 1);
  return value % 2 !== 0 ? lighten(colour, n) : darken(colour, n);
};

export const isPairingDecorator = (dimension: ApiDimension) => {
  return [
    ApiDimension.DIMENSION_LOCATION_TAGGER,
    ApiDimension.DIMENSION_LANDING_PAGE_LOCATION_TAGGER,
    ApiDimension.DIMENSION_LANDING_NEXT_PAGE_LOCATION_TAGGER,
  ].includes(dimension);
};

export const pairingDecorator = (
  dimension: string | ApiDimension,
): ApiDimension | null => {
  switch (dimension) {
    case ApiDimension.DIMENSION_LOCATION_TAGGER_VALUE:
      return ApiDimension.DIMENSION_LOCATION_TAGGER;
    case ApiDimension.DIMENSION_LANDING_PAGE_LOCATION_TAGGER_VALUE:
      return ApiDimension.DIMENSION_LANDING_PAGE_LOCATION_TAGGER;
    case ApiDimension.DIMENSION_LANDING_NEXT_PAGE_LOCATION_TAGGER_VALUE:
      return ApiDimension.DIMENSION_LANDING_NEXT_PAGE_LOCATION_TAGGER;
    default:
      return null;
  }
};

export const removeFirstDimension = (
  data: TChartData[],
  dimensions: ApiDimension[],
) =>
  data.map((d) => {
    let o: TChartData = {
      name: d.name,
    };
    for (const key in d) {
      if (key === dimensions[0]) {
        continue;
      }

      o[key] = d[key];
    }

    return o;
  });

const trendDimensions = [
  ApiDimension.DIMENSION_SESSION_START_BY_HOUR,
  ApiDimension.DIMENSION_SESSION_START_BY_DAY,
  ApiDimension.DIMENSION_SESSION_START_BY_WEEK,
  ApiDimension.DIMENSION_SESSION_START_BY_MONTH,
  ApiDimension.DIMENSION_SESSION_START_DATE,
  ApiDimension.DIMENSION_FIRST_SALE_DATE,
  ApiDimension.DIMENSION_FIRST_SALE_MONTH,
];

export const filterTrendDimensions = (dimensions: ApiDimension[]) =>
  (dimensions || []).filter((d) => trendDimensions.includes(d));

export const hasTrendDimensions = (dimensions: ApiDimension[]) =>
  filterTrendDimensions(dimensions).length > 0;

export const hasDayTrend = (dimensions: ApiDimension[]) =>
  (dimensions || []).filter((d) => {
    return [ApiDimension.DIMENSION_SESSION_START_BY_DAY].includes(d);
  }).length > 0;

export const hasHourTrend = (dimensions: ApiDimension[]) =>
  (dimensions || []).filter((d) => {
    return [ApiDimension.DIMENSION_SESSION_START_BY_HOUR].includes(d);
  }).length > 0;

export const filterNonTrendDimensions = (dimensions: ApiDimension[]) =>
  (dimensions || []).filter((d) => {
    return !trendDimensions.includes(d);
  });

export const isTrendDimension = (dimension: ApiDimension) =>
  trendDimensions.includes(dimension);

// sees if string passed is a dimension
export const isDimension = (value: string) =>
  enumToArray(ApiDimension).includes(value as ApiDimension);

export const isAverageMetric = (metric: ApiMetric) =>
  [
    ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
    ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE,
    ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE_DURING_SESSION,
    ApiMetric.METRIC_AVERAGE_SESSION_DURATION,
    ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
    ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE_DURING_SESSION,
    ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_VISIT,
    ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE,
    ApiMetric.METRIC_AVERAGE_REPEAT_CUSTOMER_ORDERS,
    ApiMetric.METRIC_AVERAGE_EFFECTIVE_PAGEVIEWS_COUNT_DURING_SESSION,
    ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE,
    ApiMetric.METRIC_AVERAGE_SALE_VALUE,
    ApiMetric.METRIC_AVERAGE_SCROLL_SPEED,
    ApiMetric.METRIC_AVERAGE_SCROLL_SPEED_DURING_SESSION,
    ApiMetric.METRIC_AVERAGE_EFFECTIVE_PAGEVIEWS_RATE_DURING_SESSION,
  ].includes(metric);

export const isPercentageMetric = (metric: ApiMetric) =>
  [
    ApiMetric.METRIC_RETURNING_LEAD_PERCENTAGE,
    ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
    ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE_RANK,
    ApiMetric.METRIC_PAGE_MACRO_CONVERSION_RATE,
    ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
    ApiMetric.METRIC_MACRO_CONVERSION_RATE,
    ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_TO_CONVERSION_RATE,
    ApiMetric.METRIC_EFFECTIVE_SESSIONS_TO_CONVERSION_RATE,
  ].includes(metric);

export const getUniqueMetrics = (data: any[]) => [
  ...new Set(
    data
      .map((s) => {
        const { name, ...rest } = s;
        return Object.keys(rest);
      })
      .flat(),
  ),
];
export const getUniqueDimensionValues = (
  rows: ApiRow[],
  startIndex?: number,
) => {
  const dims = new Set();
  (rows || []).forEach((row) => {
    (row.dimensions || []).forEach((d, i) => {
      if (!startIndex || i >= startIndex) {
        dims.add(d);
      }
    });
  });
  return [...dims];
};

export const remapYaxisFromUniqueMetrics = (
  ykeys: ApiChartYAxisKey[],
  uniqueMetrics: string[],
): ApiChartYAxisKey[] => {
  return ykeys
    .map((yKey) =>
      produce(yKey, (draft) => {
        if (!isArrayEmpty(draft.fields)) {
          let mappedFields = (draft.fields || [])
            .map((f) => {
              const arr: any[] = [];
              uniqueMetrics.forEach((uniq) => {
                if (uniq.includes(f)) {
                  arr.push(uniq);
                }
              });
              return arr;
            })
            .flat();
          draft.fields = mappedFields;
        }

        draft.chart = Object.assign(
          {},
          ...Object.keys(draft.chart || {})
            .filter((k) => {
              return draft.chart && draft.chart.hasOwnProperty(k);
            })
            .map((k) => {
              const value = draft.chart![k];
              let o: any = {};
              uniqueMetrics.forEach((uniq) => {
                if (uniq.includes(k)) {
                  o[uniq] = value;
                }
              });
              return o;
            })
            .flat(),
        );
      }),
    )
    .filter((v) => v);
};

export const getFirstMetricFromY = (yAxisKeys?: IYKey[]) => {
  return getMetricByIndex(0, yAxisKeys);
};

export const getChartMetricValue = (
  row: TChartData | undefined,
  metric: ApiMetric | string,
): number => {
  if (!row) {
    return 0;
  }

  const raw = row[metric];
  if (raw == null) {
    return 0;
  }

  if (typeof raw === "object" && "value" in raw) {
    return Number((raw as TChartDataCompare).value) || 0;
  }

  return Number(raw) || 0;
};

export const getMetricByIndex = (index: number, yAxisKeys?: IYKey[]) => {
  if (!yAxisKeys) {
    return;
  }
  const y = yAxisKeys[0];
  if (!y) {
    return;
  }
  if (typeof y === "string") {
    return y[index] as ApiMetric;
  }
  return (y.fields || [])[index] as ApiMetric;
};

export const percentageTopOfTotal = (data: TChartData[], metric: ApiMetric) => {
  const total = (data || []).reduce(
    (agg, v) => agg + Number(v[metric as ApiMetric] || 0),
    0,
  );

  const value = data[0] ? data[0][metric] : 0;
  return percentageOf(Number(value), total);
};

export const percentageTopChangeByMetric = (
  data: TChartData[],
  comp: TChartData[],
  metric: ApiMetric,
) => {
  const top = data[0] ? data[0][metric] : 0;
  const toCompare = comp.find((c) => c.name === data[0]?.name);
  if (!toCompare) {
    return 0;
  }

  return percentageChange(Number(toCompare[metric]), Number(top));
};
export const percentageChangeByRowByMetric = (
  data: TChartData,
  comp: TChartData[],
  metric: ApiMetric,
) => {
  const top = Number(data[metric]) || 0;
  const toCompare = comp.find((c) => c.name === data.name);
  if (!toCompare) {
    return 0;
  }

  return percentageChange(Number(toCompare[metric]), Number(top));
};

export const mostImprovedByMetric = (
  data: TChartData[],
  comp: TChartData[],
  metric: ApiMetric,
): TChartData => {
  const improvements = dataWithImprovement(data, comp, metric);

  // Find the item with the greatest improvement
  const { improvement, ...row } = improvements.reduce(
    (prev, current) =>
      prev.improvement > current.improvement ? prev : current,
    [] as any,
  );
  return row as TChartData;
};

export const biggestRegressionByMetric = (
  data: TChartData[],
  comp: TChartData[],
  metric: ApiMetric,
): TChartData => {
  const regressions = dataWithImprovement(data, comp, metric);

  // Find the item with the greatest regression
  const { improvement, ...row } = regressions.reduce(
    (prev, current) =>
      prev.improvement < current.improvement ? prev : current,
    [] as any,
  );
  return row as TChartData;
};

const dataWithImprovement = (
  data: TChartData[],
  comp: TChartData[],
  metric: ApiMetric,
) => {
  return (data || []).map((currentItem) => {
    const previousItem = comp.find(
      (prevItem) => prevItem.name === currentItem.name,
    );
    if (previousItem) {
      return {
        ...currentItem,
        improvement: Number(currentItem[metric]) - Number(previousItem[metric]),
      };
    } else {
      // Handle the case where the corresponding previous data doesn't exist
      return {
        ...currentItem,
        improvement: 0, // No improvement if previous data is missing
      };
    }
  });
};

export const mergeApiChartYAxisKeys = <T extends ApiChartYAxisKey>(
  arr1: T[],
  arr2: T[],
): T[] => {
  const mergedArray: T[] = [];
  const mergedKeys: Set<string> = new Set();

  // Merge objects with the same 'key' property
  for (const obj1 of arr1) {
    if (obj1?.key && !mergedKeys.has(obj1.key)) {
      const matchingObj2 = arr2.find((obj2) => obj2.key === obj1.key);

      if (matchingObj2) {
        mergedArray.push({
          ...obj1,
          fields: [...(obj1.fields || []), ...(matchingObj2.fields || [])],
          chart: {
            ...(obj1.chart || {}),
            ...(matchingObj2.chart || {}),
          },
        });
        mergedKeys.add(obj1.key);
      } else {
        mergedArray.push(obj1);
      }
    }
  }

  // Add remaining objects from arr2 that have unique keys
  for (const obj2 of arr2) {
    if (obj2?.key && !mergedKeys.has(obj2.key)) {
      mergedArray.push(obj2);
      mergedKeys.add(obj2.key);
    }
  }

  return mergedArray;
};

export const isJourneyDimension = (dimension: ApiDimension) =>
  [
    ApiDimension.DIMENSION_CONVERTING_SESSION_JOURNEY,
    ApiDimension.DIMENSION_CHANNEL_MACRO_CONVERSION_JOURNEY,
    ApiDimension.DIMENSION_SALES_SESSION_JOURNEY,
    ApiDimension.DIMENSION_CHANNEL_SALES_JOURNEY,
  ].includes(dimension);

export const metricsFromChart = (chart: IChart) => {
  let metrics: ApiMetric[] = [];
  for (let i = 0; i < (chart.yAxisKeys || []).length; i++) {
    const y = (chart.yAxisKeys || [])[i];
    if (typeof y === "string") {
      metrics.push(y as ApiMetric);
    } else {
      metrics.push(...((y.fields || []) as ApiMetric[]));
    }
  }
  return metrics;
};

export const mergeComparisonData = (
  data: TChartData[],
  comparisonData: TChartData[],
) => {
  const tickedData = buildTicks(data);
  const tickedComparisonData = buildTicks(comparisonData);
  return mergeArraysByKey(tickedData, tickedComparisonData, "tick");
};

const attributionMetricOrder = {
  [AttributionMetric.MACRO]: 1,
  [AttributionMetric.SALES]: 2,
  [AttributionMetric.REVENUE]: 3,
  [AttributionMetric.LTV]: 4,
  [AttributionMetric.ROAS]: 5,
  [AttributionMetric.ROASLTV]: 6,
  [AttributionMetric.ROI]: 7,
  [AttributionMetric.ROILTV]: 8,
};

const attributionTypeOrder = {
  [ApiAttributionModel.EBA]: 1,
  [ApiAttributionModel.FIRST]: 2,
  [ApiAttributionModel.LAST]: 3,
  [ApiAttributionModel.LAST_NON_DIRECT]: 4,
  [ApiAttributionModel.LINEAR]: 5,
  [ApiAttributionModel.POSITION_BASED]: 6,
  [ApiAttributionModel.IMPACTED]: 7,
  [ApiAttributionModel.CUSTOM]: 8,
};

export const orderAttributionType = (types: ApiAttributionModel[]) => {
  return [...types].sort(
    (a, b) => attributionTypeOrder[a] - attributionTypeOrder[b],
  );
};

export const orderAttributionMetrics = (metrics: ApiMetric[]) => {
  return metrics.sort((a, b) => {
    const metricConfigA = metricConfigByName(a);
    const metricConfigB = metricConfigByName(b);
    if (!metricConfigA?.attribtionMetric || !metricConfigB?.attribtionMetric) {
      return 0;
    }
    // Compare by attributionMetric first
    const metricA = metricConfigA?.attribtionMetric;
    const metricB = metricConfigB?.attribtionMetric;
    if (attributionMetricOrder[metricA] < attributionMetricOrder[metricB]) {
      return -1;
    } else if (
      attributionMetricOrder[metricA] > attributionMetricOrder[metricB]
    ) {
      return 1;
    }

    if (!metricConfigA?.attribtionType || !metricConfigB?.attribtionType) {
      return 0;
    }

    // If the attributionMetric is the same, compare by attributionType
    const typeA = metricConfigA?.attribtionType;
    const typeB = metricConfigB?.attribtionType;
    return attributionTypeOrder[typeA] - attributionTypeOrder[typeB];
  });
};

export const nonEmptyDimensions = (
  dimensions: (ApiDimension | string)[] | undefined,
) => {
  return (dimensions || []).filter(
    (d) => d !== "" && d !== ApiDimension.DIMENSION_NOT_SET,
  );
};

export const attributesFromChartDataRow = (
  data: TChartData,
): ApiChartAttribute[] => {
  const ats: ApiChartAttribute[] = [];
  Object.keys(data).forEach((key) => {
    if (isDimension(key)) {
      ats.push({
        key,
        operator: ApiOperator.EQUAL,
        value: `${data[key]}`,
      });
    }
  });
  return ats;
};

export const mergeDimensionToMetrics = (
  groupedData: Record<string, TChartData[]>,
  lastNonTrendDimension: ApiDimension,
  prefix?: string,
) => {
  return Object.keys(groupedData).map((key) => {
    let o: any = {
      name: key,
    };
    groupedData[key].forEach((g) => {
      const { name, tick, ...rest } = g;
      o["tick"] = tick;
      Object.keys(rest).forEach((metricKey) => {
        if (metricKey === lastNonTrendDimension) {
          return;
        }
        o[`${prefix || ""}${rest[lastNonTrendDimension] || ""}\\${metricKey}`] =
          rest[metricKey];
      });
    });

    return o;
  });
};

export const buildTicks = (chartData: TChartData[]) => {
  // Extract unique values that are valid dates
  const dateNames = Array.from(
    new Set(
      chartData
        .map((d) => d.name)
        .filter((name) => !isNaN(new Date(name).valueOf())),
    ),
  );

  // Sort the date names chronologically
  dateNames.sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());

  // Create a mapping of date to ordinal position
  const dateMap = new Map<string, number>();
  dateNames.forEach((name, index) => {
    dateMap.set(name, index);
  });

  // Map data to include the corrected tick values
  return chartData.map((d) => {
    const isDate = !isNaN(new Date(d.name).valueOf());
    const dateOrdinal = isDate ? dateMap.get(d.name) : d.name; // Use ordinal if date, else keep the original value

    let ticks = [dateOrdinal];
    Object.keys(d).forEach((k) => {
      if (isDimension(k) && !isTrendDimension(k as ApiDimension)) {
        ticks.push(`${d[k]}`);
      }
    });

    return { ...d, tick: ticks.join("+") };
  });
};

export const displayDataMapFn = (row) => {
  return Object.fromEntries(
    Object.entries(row).filter(
      ([key]) =>
        key !== "tick" && !key.endsWith("_tick") && !key.endsWith("\\tick"),
    ),
  ) as TChartData;
};

export const groupWithPrevious = (arr: any, key?: string) => {
  // strip our previous into new array
  const previous: any[] = [];
  return arr
    .filter((a) => {
      if (a[key || "name"].startsWith("previous_")) {
        previous.push(a);
        return false;
      }
      return true;
    })
    .map((a) => {
      const found = previous.find((p) =>
        p[key || "name"].endsWith(a[key || "name"]),
      );
      if (found) {
        return { ...a, previous: found };
      }
      return a;
    });
};

export const incDec = (
  comp: number = 0,
  curr: number = 0,
  isPercentage?: boolean,
): {
  direction?: "up" | "down";
  value: number;
} => {
  const change = isPercentage ? curr - comp : percentageChange(comp, curr);
  return {
    value: Number(formatNumber(change || 0)),
    direction:
      Math.sign(change) > 0 ? "up" : Math.sign(change) < 0 ? "down" : undefined,
  };
};

export const uniqueMetricsFromChart = (chart: IChart) => {
  return chart.yAxisKeys
    .map((y) => {
      let arr: string[] = [];
      if (typeof y === "string") {
        arr = [y];
      } else {
        arr = ((y as ApiChartYAxisKey).fields || []).filter((v) => !!v);
      }
      return arr;
    })
    .flat();
};

export const replaceAttributionMetricsInMetrics = (
  metrics: ApiMetric[],
  models: ApiAttributionModel[],
  chart?: { [key: string]: ApiYAxisChartType },
) => {
  if (!metrics || models.length === 0) {
    return { metrics: metrics || [], chart: chart || {} };
  }

  const result: ApiMetric[] = [];
  const chartConfig = { ...chart };

  metrics.forEach((metric) => {
    const config = metricConfigByName(metric);

    // If it's not an attribution metric or has a specific attribution type, keep as-is
    if (
      !config?.attribtionType ||
      config.attribtionType !== ApiAttributionModel.NO_MODEL_SELECTED
    ) {
      result.push(metric);
      return;
    }

    // Replace attribution metric with models
    models.forEach((model) => {
      const attributionMetric = metricByAttribution(
        model,
        config.attribtionMetric as AttributionMetric,
      ) as ApiMetric;

      result.push(attributionMetric);

      // Copy chart config from original metric
      if (chart && chart[metric]) {
        chartConfig[attributionMetric] = chart[metric];
      }
    });
  });

  return {
    metrics: uniqueArray(result),
    chart: chartConfig,
  };
};

export const replaceAttributionMetricsInChart = (
  chart: IChart,
  models: ApiAttributionModel[],
) => {
  return produce(chart, (draft) => {
    if (models.length === 0) {
      return;
    }

    draft.yAxisKeys.forEach((y, yidx) => {
      if (typeof y === "string") {
        return;
      }

      let fields = y.fields || [];
      const { metrics, chart } = replaceAttributionMetricsInMetrics(
        fields as ApiMetric[],
        models,
        y.chart,
      );
      (draft.yAxisKeys[yidx] as ApiChartYAxisKey).fields = metrics;
      if (chart) {
        (draft.yAxisKeys[yidx] as ApiChartYAxisKey).chart = chart;
      }
    });
  });
};

export const metricCategoryTitle = (cat: ApiMetricCategory) => {
  return camelToText(cat.replace("MetricCategory", "")) + " Metrics";
};

export const orderGroupedMetricLabels = (labels: ApiSimpleMetricLabel[]) => {
  const categoryOrder: Record<string, number> = {
    [ApiMetricCategory.METRIC_CATEGORY_TRAFFIC]: 1,
    [ApiMetricCategory.METRIC_CATEGORY_ENGAGEMENT]: 2,
    [ApiMetricCategory.METRIC_CATEGORY_ECOMMERCE]: 3,
    [ApiMetricCategory.METRIC_CATEGORY_ATTRIBUTION]: 4,
    [ApiMetricCategory.METRIC_CATEGORY_CONVERSION]: 5,
    [ApiMetricCategory.METRIC_CATEGORY_COST_EFFICIENCY]: 6,
    [ApiMetricCategory.METRIC_CATEGORY_CUSTOMER_LIFETIME_VALUE]: 7,
    [ApiMetricCategory.METRIC_CATEGORY_PUBLISHER_DATA]: 8,
    [ApiMetricCategory.METRIC_CATEGORY_RETENTION]: 9,
    [ApiMetricCategory.METRIC_CATEGORY_ADVANCED]: 10,
    [ApiMetricCategory.METRIC_CATEGORY_VELOCITY]: 11,
    [ApiMetricCategory.METRIC_CATEGORY_UNKNOWN]: 100,
  };
  const sorted = sortAlpha([...labels], "label").sort((a, b) => {
    const aCategoryOrder = categoryOrder[a.category ?? ""] ?? 99;
    const bCategoryOrder = categoryOrder[b.category ?? ""] ?? 99;

    if (aCategoryOrder !== bCategoryOrder) {
      return aCategoryOrder - bCategoryOrder;
    }

    return 0;
  });
  return sorted;
};

export const groupMetricsByCategory = (
  metrics: ApiMetric[],
  metricLabels: ApiSimpleMetricLabel[],
) => {
  const filtered = metricLabels.filter(
    (label) => label.metric && metrics.includes(label.metric),
  );
  const sorted = orderGroupedMetricLabels(filtered);
  const categoryOrder: string[] = [];
  const groups: Record<string, ApiMetric[]> = {};
  sorted.forEach((label) => {
    const cat = label.category ?? ApiMetricCategory.METRIC_CATEGORY_UNKNOWN;
    if (!groups[cat]) {
      groups[cat] = [];
      categoryOrder.push(cat);
    }
    groups[cat].push(label.metric as ApiMetric);
  });
  return categoryOrder.map((cat) => ({
    name: metricCategoryTitle(cat as ApiMetricCategory),
    metrics: groups[cat],
  }));
};

export const dimensionCategoryTitle = (cat: ApiDimensionCategory) => {
  return camelToText(cat.replace("DimensionCategory", ""));
};

export const orderGroupedDimensionLabels = (
  labels: ApiSimpleDimensionLabel[],
) => {
  const categoryOrder: Record<string, number> = {
    [ApiDimensionCategory.DIMENSION_CATEGORY_TRAFFIC_AND_CAMPAIGNS]: 1,
    [ApiDimensionCategory.DIMENSION_CATEGORY_LANDING_PAGES]: 2,
    [ApiDimensionCategory.DIMENSION_CATEGORY_CONTENT_AND_CONVERSION_JOURNEYS]: 4,
    [ApiDimensionCategory.DIMENSION_CATEGORY_CONVERSIONS_AND_INTERACTIONS]: 5,
    [ApiDimensionCategory.DIMENSION_CATEGORY_DEVICE_AND_TECHNOLOGY]: 6,
    [ApiDimensionCategory.DIMENSION_CATEGORY_PRODUCTS]: 7,
    [ApiDimensionCategory.DIMENSION_CATEGORY_USER_DATA_AND_DEMOGRAPHICS]: 8,
    [ApiDimensionCategory.DIMENSION_CATEGORY_AUDIENCE_SEGMENTS]: 9,
    [ApiDimensionCategory.DIMENSION_CATEGORY_SESSION_TIMING]: 10,
    [ApiDimensionCategory.DIMENSION_CATEGORY_INCENDIUM_CAMPAIGNS]: 11,
    [ApiDimensionCategory.DIMENSION_CATEGORY_JOB_DATA]: 12,
    [ApiDimensionCategory.DIMENSION_CATEGORY_DOMAIN]: 13,
    [ApiDimensionCategory.DIMENSION_CATEGORY_CRAWLERS]: 14,
    [ApiDimensionCategory.DIMENSION_CATEGORY_TRANSACTIONS]: 15,
    [ApiDimensionCategory.DIMENSION_CATEGORY_UNKNOWN]: 16,
  };
  const sorted = sortAlpha([...labels], "label").sort((a, b) => {
    const aCategoryOrder = categoryOrder[a.category ?? ""] ?? 99;
    const bCategoryOrder = categoryOrder[b.category ?? ""] ?? 99;

    if (aCategoryOrder !== bCategoryOrder) {
      return aCategoryOrder - bCategoryOrder;
    }

    return 0;
  });
  return sorted;
};

const defaultOrderByDimensions = [
  ApiDimension.DIMENSION_SESSION_START_BY_HOUR_OF_DAY,
  ApiDimension.DIMENSION_SESSION_START_BY_WEEKDAY,
  ApiDimension.DIMENSION_SESSION_START_BY_DAY_OF_MONTH,
  ApiDimension.DIMENSION_SESSION_START_BY_DAY_OF_YEAR,
  ApiDimension.DIMENSION_DAYS_TO_FIRST_MACRO_CONVERSION,
  ApiDimension.DIMENSION_DAYS_TO_FIRST_MACRO_CONVERSION,
  ApiDimension.DIMENSION_DAYS_TO_SECOND_VISIT,
  ApiDimension.DIMENSION_DAYS_TO_SECOND_PURCHASE,
  ApiDimension.DIMENSION_VISITS_TO_FIRST_MACRO_CONVERSION,
  ApiDimension.DIMENSION_VISITS_SINCE_PRIOR_MACRO_CONVERSION,
];

export const getAutoOrderDimension = (
  dimensions: ApiDimension[],
): ApiDimension | undefined => {
  return dimensions.find((d) => defaultOrderByDimensions.includes(d));
};
